/**
 * SampleContainerPhCheckDialog — modal popup for pH Check on the Sample
 * Registration detail page, matching XAF's `btnPH_Execute` behavior
 * (SampleContainerpHCheckViewController.cs line 529).
 *
 * Shows the SAME two editable columns the standalone Sample Container pH Check
 * list uses — Recorded pH + Adjusted pH (free text, may carry a qualifier like
 * "<26") — so a value entered here reflects straight into that list (both write
 * dbo.SampleBottleAllocation.RecordedpH / AdjustedpH). The legacy single
 * `pHCheck` field is preserved untouched on save (owner request 2026-10-23:
 * "put the two columns here instead of pHCheck, it should affect the Sample
 * Container pH Check list").
 *
 *  · Fetches `GET /api/v1/sample-container-ph-check/by-job?jobId=...`
 *  · AppDataGrid with SampleID, ContainerID, Recorded pH, Adjusted pH
 *  · Both pH cells inline-editable (batch-edit); Save sends PUT per edited row
 *  · Footer "Copy To All Selected" copies both pH values across selected rows
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import { useNavActions } from '../../../hooks/useNavActions';
import { LoaderSymbol } from '../../../components/loading';

interface PhRow {
  oid:         string;
  jobId:       string;
  sampleId:    string;
  division:    string;
  containerId: string;
  phCheck:     string | null;   // legacy single field — preserved untouched on save
  recordedPh:  string | null;   // free text (may carry a qualifier, e.g. "<26")
  adjustedPh:  string | null;
  statusName:  string;
  statusId:    string;
}

/** A row's staged (unsaved) edits — only the two user-editable pH fields. */
type RowEdit = { recordedPh?: string; adjustedPh?: string };

interface Props {
  open: boolean;
  jobId: string;
  onClose: () => void;
}

/** The saved value of an editable field (what a staged edit is compared against). */
function savedStr(row: PhRow, field: keyof RowEdit): string {
  if (field === 'recordedPh') return row.recordedPh ?? '';
  return row.adjustedPh ?? '';
}

export function SampleContainerPhCheckDialog({ open, jobId, onClose }: Props) {
  const notify  = useNotify();
  const runSave = useSaveFlow();
  const canWrite = useNavActions('sampphcheck').write;

  const [rows,    setRows]    = useState<PhRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [edits,   setEdits]   = useState<Record<string, RowEdit>>({});
  const [saving,  setSaving]  = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await api.get('/api/v1/sample-container-ph-check/by-job', {
        params: { jobId },
        silentLoading: true,
      } as never);
      setRows((res.data as { data: PhRow[] }).data ?? []);
      setEdits({});
      setSelected(new Set());
    } catch {
      notify.error('Failed to load pH Check data.');
    } finally {
      setLoading(false);
    }
  }, [jobId, notify]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  // Click-to-edit stages a field value in the local batch buffer; a row is dirty
  // only when a staged field differs from its saved value.
  const stageEdit = useCallback((row: PhRow, field: keyof RowEdit, newValue: string) => {
    const trimmed = newValue.trim();
    setEdits(prev => {
      const next = { ...prev };
      const rowEdit: RowEdit = { ...(next[row.oid] ?? {}), [field]: trimmed };
      const dirty = (['recordedPh', 'adjustedPh'] as (keyof RowEdit)[])
        .some(f => rowEdit[f] !== undefined && rowEdit[f] !== savedStr(row, f));
      if (dirty) next[row.oid] = rowEdit;
      else delete next[row.oid];
      return next;
    });
  }, []);

  const dirtyCount = Object.keys(edits).length;

  const cancelEdits = useCallback(() => {
    setEdits({});
    setSelected(new Set());
  }, []);

  const save = useCallback(() => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Sample Container pH Check',
      auditAction: 'edit',
      save: async () => {
        for (const [oid, e] of Object.entries(edits)) {
          const row = rows.find(r => r.oid === oid);
          const effRec = (e.recordedPh ?? (row ? savedStr(row, 'recordedPh') : '')).trim();
          const effAdj = (e.adjustedPh ?? (row ? savedStr(row, 'adjustedPh') : '')).trim();
          // Preserve the legacy pHCheck field; this popup edits Recorded / Adjusted pH only.
          // Recorded / Adjusted pH are FREE TEXT — send the raw string so a qualifier
          // like "<26" survives (never coerced to a number).
          await api.put(`/api/v1/sample-container-ph-check/${oid}`, {
            phCheck:    row?.phCheck ?? null,
            recordedPh: effRec || null,
            adjustedPh: effAdj || null,
          });
        }
        await load();
      },
      successMessage: `Updated ${dirtyCount} container(s).`,
    }).finally(() => setSaving(false));
  }, [dirtyCount, edits, rows, notify, runSave, load]);

  // "Copy To All Selected" — copies the first selected row's Recorded + Adjusted
  // pH to every other selected row (XAF's "Copy To All Cell").
  const copyToAllSelected = useCallback(() => {
    if (selected.size < 2) { notify.info('Select at least two rows first.'); return; }
    const selArr = Array.from(selected);
    const sourceOid = selArr[0] as string;
    const sourceRow = rows.find(r => r.oid === sourceOid);
    if (!sourceRow) return;
    const srcRec = (edits[sourceOid]?.recordedPh ?? savedStr(sourceRow, 'recordedPh')).trim();
    const srcAdj = (edits[sourceOid]?.adjustedPh ?? savedStr(sourceRow, 'adjustedPh')).trim();
    if (!srcRec && !srcAdj) { notify.info('Source row has no pH value.'); return; }

    for (const oid of selArr) {
      if (oid === sourceOid) continue;
      const row = rows.find(r => r.oid === (oid as string));
      if (!row) continue;
      stageEdit(row, 'recordedPh', srcRec);
      stageEdit(row, 'adjustedPh', srcAdj);
    }
    notify.success(`Copied pH to ${selArr.length - 1} row(s).`);
  }, [selected, rows, edits, notify, stageEdit]);

  const cols: AppColumn<PhRow>[] = useMemo(() => [
    { field: 'sampleId',    header: 'Sample ID',    width: 140 },
    { field: 'containerId', header: 'Container ID', width: 110 },
    { field: 'recordedPh',  header: 'Recorded pH',  width: 110,
      value: r => edits[r.oid]?.recordedPh ?? savedStr(r, 'recordedPh'),
      text:  r => edits[r.oid]?.recordedPh ?? savedStr(r, 'recordedPh'),
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
    { field: 'adjustedPh',  header: 'Adjusted pH',  width: 110,
      value: r => edits[r.oid]?.adjustedPh ?? savedStr(r, 'adjustedPh'),
      text:  r => edits[r.oid]?.adjustedPh ?? savedStr(r, 'adjustedPh'),
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
  ], [edits, canWrite]);

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      {canWrite && (
        <>
          <button className="ca-btn" onClick={copyToAllSelected}
            disabled={saving || selected.size < 2}
            title="Copy the first selected row's Recorded + Adjusted pH to all other selected rows">
            <span className="tb-i">📋</span> Copy To All Selected
          </button>
          <button className="ca-btn ca-btn-solid" onClick={save}
            disabled={saving || dirtyCount === 0}>
            {saving
              ? <><LoaderSymbol size={13} /> Saving…</>
              : <><span className="tb-i">💾</span> Save</>}
          </button>
          <button className="ca-btn" onClick={cancelEdits}
            disabled={saving || dirtyCount === 0}>
            <span className="tb-i">✕</span> Cancel
          </button>
        </>
      )}
      <button className="ca-btn" onClick={onClose}>Close</button>
    </div>
  );

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title={`Sample Container pH Check — ${jobId}`}
      icon="ti-flask"
      width={560}
      onClose={onClose}
      footer={footer}
      maximizable
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <LoaderSymbol size={32} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ai-muted)' }}>
          No container pH check records for this job.
        </div>
      ) : (
        <div style={{ minHeight: 200 }}>
          <AppDataGrid
            rows={rows}
            columns={cols}
            getRowId={(r) => r.oid}
            defaultPageSize={50}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={(row, col, val) => {
              if (col.field === 'recordedPh' || col.field === 'adjustedPh') {
                stageEdit(row, col.field as keyof RowEdit, val);
              }
            }}
          />
          {dirtyCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginTop: 4 }}>
              {dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </AppDialog>
  );
}
